import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Input, Skeleton, Space, Tooltip, Typography } from 'antd';
import {
  BookOutlined,
  SearchOutlined,
  ExperimentOutlined,
  SafetyOutlined,
  WarningFilled,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import {
  getLintRuleCatalog,
  type LintRuleCatalogEntry,
} from '@/api/instructionLint';
import { RuleDetailDrawer, ruleDetailFromCatalog } from './RuleDetailDrawer';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// RuleCatalogPage — Platform → Rule Library
// ─────────────────────────────────────────────────────────────────────────────
//
// AUDIENCE
//   Tenant admins, sales / AE / CSM, agent designers, clinical SMEs.
//   "What are the authoring rules this platform enforces?"
//
//   The lint findings page (Workspace → Instruction Lint) is the per-agent
//   *action* surface. This page is the *reference* surface — every rule on
//   the platform, browseable, with the same drawer copy as the findings page.
//
// LAYOUT
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ PageHeader — "Rule Library" + count                              │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ Search box · Category filter chips                               │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ Rule cards (one per rule):                                       │
//   │   • Rule code (mono) · Title                                     │
//   │   • Category chip                                                │
//   │   • One-line WhatItChecks summary                                │
//   │   • "View →" affordance (whole card is clickable)                │
//   └──────────────────────────────────────────────────────────────────┘
//
// CARD CLICK → opens the existing RuleDetailDrawer (reused from findings page).
// URL ANCHOR `/platform/rules#G-03` → page mounts, finds matching rule, opens drawer.
// ─────────────────────────────────────────────────────────────────────────────

export const RuleCatalogPage = observer(function RuleCatalogPage() {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [rules, setRules] = useState<LintRuleCatalogEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openRule, setOpenRule] = useState<LintRuleCatalogEntry | null>(null);

  // Refs to each rule card for hash-anchor scrolling. Map keyed by ruleId.
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Tracks whether we've already auto-opened the drawer from the URL hash —
  // we only do it once per mount so subsequent in-page clicks aren't fought.
  const hashHandledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await getLintRuleCatalog();
        if (!cancelled) setRules(r);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load rule catalog');
          setRules(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hash-anchor handler — when the URL is /platform/rules#G-03 and the
  // catalog has loaded, auto-scroll to the card AND open its drawer.
  // The drawer's footer link uses this exact pattern to deep-link callers.
  useEffect(() => {
    if (!rules || hashHandledRef.current) return;
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!hash) return;
    const match = rules.find((r) => r.id === hash);
    if (!match) return;
    hashHandledRef.current = true;
    // Defer to next tick so the cards are rendered and refs populated.
    setTimeout(() => {
      cardRefs.current[match.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setOpenRule(match);
    }, 50);
  }, [rules]);

  // Derived: distinct categories present in the catalog (for the filter chips).
  const categories = useMemo(() => {
    if (!rules) return [];
    const set = new Set<string>();
    rules.forEach((r) => set.add(r.category));
    return Array.from(set).sort();
  }, [rules]);

  // Derived: filtered + searched rules.
  const filtered = useMemo(() => {
    if (!rules) return [];
    const q = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (activeCategory && r.category !== activeCategory) return false;
      if (!q) return true;
      // Match against id, title, what-it-checks. Whitespace-insensitive
      // substring search — operators paste rule codes ("g-03") or keywords
      // ("escalation") indifferently.
      return (
        r.id.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.whatItChecks ?? '').toLowerCase().includes(q)
      );
    });
  }, [rules, search, activeCategory]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        accent="violet"
        icon={<BookOutlined />}
        title="Rule Library"
        subtitle="Every authoring rule the platform checks agents against. Same copy you see in the lint findings drawer — single source of truth."
      />

      {/* ─── Search + filter bar ──────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          padding: '12px 14px',
          background: isDark ? colors.slate[800] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 8,
        }}
      >
        <Input
          allowClear
          placeholder="Search rule code or keyword (e.g. 'G-03', 'escalation', 'citation')"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Space size={6} wrap>
          <CategoryChip
            label="All"
            active={activeCategory == null}
            isDark={isDark}
            onClick={() => setActiveCategory(null)}
          />
          {categories.map((cat) => (
            <CategoryChip
              key={cat}
              label={cat}
              active={activeCategory === cat}
              isDark={isDark}
              onClick={() => setActiveCategory(cat)}
            />
          ))}
        </Space>
        <span style={{ flex: 1 }} />
        {rules && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {filtered.length} of {rules.length} rules
          </Text>
        )}
      </div>

      {/* ─── Body ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert
          type="error"
          showIcon
          message="Couldn't load rule catalog"
          description={error}
          closable
          onClose={() => setError(null)}
        />
      )}

      {loading && (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      )}

      {!loading && !error && rules && filtered.length === 0 && (
        <SectionCard>
          <BrandedEmpty
            icon={<SearchOutlined />}
            title="No rules match"
            description={
              search || activeCategory
                ? 'Try a different search term or clear the category filter.'
                : 'The rule catalog is empty.'
            }
            size="md"
          />
        </SectionCard>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
            gap: 12,
          }}
        >
          {filtered.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              isDark={isDark}
              onClick={() => setOpenRule(rule)}
              cardRef={(el) => {
                cardRefs.current[rule.id] = el;
              }}
            />
          ))}
        </div>
      )}

      {/* Rule-detail drawer — reused from the lint findings page.
          showCatalogLink={false} prevents the circular "Open in Rule Library"
          link since we ARE in the Rule Library. */}
      <RuleDetailDrawer
        detail={openRule ? ruleDetailFromCatalog(openRule) : null}
        onClose={() => setOpenRule(null)}
        isDark={isDark}
        showCatalogLink={false}
      />
    </div>
  );
});

// ─── One rule card ─────────────────────────────────────────────────────────

function RuleCard({
  rule,
  isDark,
  onClick,
  cardRef,
}: {
  rule: LintRuleCatalogEntry;
  isDark: boolean;
  onClick: () => void;
  cardRef: (el: HTMLDivElement | null) => void;
}) {
  const cat = categoryPalette(rule.category, isDark);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={`Open rule details for ${rule.id} — ${rule.title}`}
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.violet[600];
        e.currentTarget.style.boxShadow = `0 1px 3px ${isDark ? '#0008' : '#0002'}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isDark
          ? colors.slate[700]
          : colors.slate[200];
        e.currentTarget.style.boxShadow = 'none';
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = colors.violet[600];
        e.currentTarget.style.outline = 'none';
        e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.violet[100]}`;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = isDark
          ? colors.slate[700]
          : colors.slate[200];
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            padding: '2px 7px',
            borderRadius: 4,
            background: isDark ? colors.slate[700] : colors.slate[100],
            color: isDark ? colors.slate[300] : colors.slate[600],
            fontWeight: 600,
          }}
        >
          {rule.id}
        </span>
        <Text
          strong
          style={{
            fontSize: 14,
            color: isDark ? colors.slate[50] : colors.slate[900],
          }}
        >
          {rule.title}
        </Text>
      </div>

      <Tooltip
        title={
          rule.categoryDescription ??
          `Authoring rules grouped under "${rule.category}".`
        }
        mouseEnterDelay={0.1}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: 999,
            background: cat.bg,
            color: cat.fg,
            alignSelf: 'flex-start',
            cursor: 'help',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {cat.icon}
          {rule.category}
        </span>
      </Tooltip>

      {rule.whatItChecks && (
        <Paragraph
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.5,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
          ellipsis={{ rows: 2 }}
        >
          {rule.whatItChecks}
        </Paragraph>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12,
          color: colors.violet[600],
          fontWeight: 500,
        }}
      >
        View
        <ArrowRightOutlined style={{ fontSize: 11 }} />
      </div>
    </div>
  );
}

// ─── Category filter chip ─────────────────────────────────────────────────

function CategoryChip({
  label,
  active,
  isDark,
  onClick,
}: {
  label: string;
  active: boolean;
  isDark: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 12,
        fontWeight: 500,
        padding: '4px 12px',
        borderRadius: 999,
        background: active
          ? colors.violet[600]
          : isDark
            ? colors.slate[700]
            : colors.slate[100],
        color: active ? '#FFFFFF' : isDark ? colors.slate[200] : colors.slate[700],
        border: 'none',
        cursor: 'pointer',
        transition: 'background 120ms ease',
      }}
    >
      {label}
    </button>
  );
}

// ─── Category palette (shared shape with the drawer; small enough not to extract) ─

function categoryPalette(
  category: string,
  isDark: boolean,
): { icon: React.ReactNode; fg: string; bg: string } {
  const map: Record<string, { icon: React.ReactNode; fg: string; bg: string }> = {
    Grounding: {
      icon: <ExperimentOutlined aria-hidden />,
      fg: colors.violet[700],
      bg: isDark ? `${colors.violet[700]}22` : colors.violet[100],
    },
    'Clinical safety': {
      icon: <SafetyOutlined aria-hidden />,
      fg: colors.red[700],
      bg: isDark ? `${colors.red[700]}22` : colors.red[100],
    },
    'Anti-patterns': {
      icon: <WarningFilled aria-hidden />,
      fg: colors.amber[700],
      bg: isDark ? `${colors.amber[700]}22` : colors.amber[100],
    },
  };
  return (
    map[category] ?? {
      icon: null,
      fg: isDark ? colors.slate[300] : colors.slate[600],
      bg: isDark ? colors.slate[700] : colors.slate[100],
    }
  );
}
